/*eslint-disable*/
import React, { memo, useState, forwardRef, useRef, useEffect } from 'react';
import MaskedTextInput from 'react-text-mask';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import * as Styled from './styles';
import moment from 'moment';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const getYearRange = (minDate, maxDate) => {
  const currentYear = new Date().getFullYear();
  const startYear = minDate?.getFullYear() > 2005 ? minDate.getFullYear() : 2005;
  const endYear = maxDate ? maxDate.getFullYear() : currentYear;
  const years = [];
  for (let year = endYear; year >= startYear; year--) {
    years.push(year);
  }
  return years;
};

const CustomDropdown = ({ value, options, onChange, ariaLabel, tabIndex }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsDropdownOpen(!isDropdownOpen);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  const handleOptionClick = (optionValue) => {
    onChange(optionValue);
    setIsDropdownOpen(false);
  };

  const handleOptionKeyDown = (e, optionValue) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOptionClick(optionValue);
    }
  };

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Styled.CustomDropdownWrapper ref={dropdownRef}>
      <Styled.DropdownButton
        type="button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        aria-expanded={isDropdownOpen}
      >
        {selectedOption?.label}
        <Styled.DropdownArrow isOpen={isDropdownOpen} />
      </Styled.DropdownButton>
      {isDropdownOpen && (
        <Styled.DropdownList>
          {options.map((option) => (
            <Styled.DropdownOption
              key={option.value}
              onClick={() => handleOptionClick(option.value)}
              onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
              tabIndex={0}
              isSelected={option.value === value}
            >
              {option.label}
            </Styled.DropdownOption>
          ))}
        </Styled.DropdownList>
      )}
    </Styled.CustomDropdownWrapper>
  );
};

const Picker = ({
  label,
  selected,
  required = false,
  onChange: handleDateChange,
  dateFormatCalendar,
  maxDate,
  minDate,
  showMonthYearDropdown,
  error,
  isFullWidth,
  isMargin = false,
  idInput = '',
  inputFor = '',
  isActive = false,
  pageColumns,
  hasFutureValidation,
  onInvalidDate,
  ...props
}) => {
  const [isFocus, setIsFocus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Handle outside clicks manually with a more robust approach
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!isOpen) return; // Only check if datepicker is open

      const target = event.target;

      // Check if click is outside our component AND outside the datepicker popup
      const isOutsideWrapper = wrapperRef.current && !wrapperRef.current.contains(target);
      const isOutsidePopup =
        !target.closest('.react-datepicker') &&
        !target.closest('.react-datepicker-popper') &&
        !target.closest('.react-datepicker__portal');

      if (isOutsideWrapper && isOutsidePopup) {
        setIsOpen(false);
        if (datePickerRef.current) {
          datePickerRef.current.setOpen(false);
        }
      }
    };

    // Use capture phase to catch the event before it gets stopped
    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [isOpen]);

  const handleKeyPress = (e, onClick) => {
    e.keyCode === 13 && onClick();
  };

  const handleCalendarClose = () => {
    setIsFocus(true);
    setIsOpen(false);
  };

  const handleCalendarOpen = () => {
    setIsOpen(true);
  };

  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    const years = getYearRange(minDate, maxDate);

    const monthOptions = MONTHS.map((month, index) => ({ value: index, label: month }));
    const yearOptions = years.map((year) => ({ value: year, label: String(year) }));

    return (
      <Styled.CustomHeaderWrapper>
        <Styled.DropdownsContainer>
          <CustomDropdown
            value={date.getMonth()}
            options={monthOptions}
            onChange={(value) => changeMonth(value)}
            tabIndex={0}
            ariaLabel="Select month"
          />

          <CustomDropdown
            value={date.getFullYear()}
            options={yearOptions}
            onChange={(value) => changeYear(value)}
            tabIndex={0}
            ariaLabel="Select year"
          />
        </Styled.DropdownsContainer>

        <Styled.NavigationContainer>
          <Styled.HeaderNavButton
            type="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            tabIndex={0}
            aria-label="Previous month"
          />
          <Styled.HeaderNavButton
            type="button"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            tabIndex={0}
            aria-label="Next month"
            isNext
          />
        </Styled.NavigationContainer>
      </Styled.CustomHeaderWrapper>
    );
  };

  // Function to open calendar when button is clicked
  const handleCalendarButtonClick = () => {
    const newOpenState = !isOpen;

    if (datePickerRef.current) {
      datePickerRef.current.setOpen(newOpenState);
    }
    setIsOpen(newOpenState);
  };

  // Function to validate if the typed date is complete and valid
  const isValidCompleteDate = (dateString) => {
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!dateRegex.test(dateString)) {
      return false;
    }

    const [month, day, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  const ExampleCustomInput = forwardRef(({ value, onClick, onChange }, ref) => {
    const handleInputChange = (e) => {
      const inputValue = e.target.value.replace(/[_]/g, '');

      if (inputValue && inputValue?.length === 10) {
        if (!isValidCompleteDate(inputValue)) {
          if (onInvalidDate) {
            // The second argument lets a caller word its own message; existing callers ignore it.
            onInvalidDate(`You have entered invalid date "${inputValue}"!. Please enter a valid date (MM/DD/YYYY)`, {
              value: inputValue,
              reason: 'invalid',
            });
            handleDateChange('');
            onChange({ target: { value: '' } });
            return;
          }
          onChange(e);
          return;
        }

        if (onInvalidDate) {
          onInvalidDate(null);
        }

        const [month, day, year] = inputValue.split('/').map(Number);
        const newDate = new Date(year, month - 1, day);

        const isWithinBounds = (!minDate || newDate >= minDate) && (!maxDate || newDate <= maxDate);

        if (isWithinBounds && !hasFutureValidation) {
          handleDateChange(newDate);
          setTimeout(() => {
            if (datePickerRef.current) {
              datePickerRef.current.setOpen(false);
            }
            setIsOpen(false);
          }, 100);
          onChange(e);
        } else if (hasFutureValidation && !isWithinBounds) {
          onInvalidDate(`The date "${inputValue}" is past current day!`);
          handleDateChange('');
          onChange({ target: { value: '' } });
        } else if (onInvalidDate && !isWithinBounds) {
          // A real date outside minDate/maxDate: say so instead of silently dropping it.
          const bound = (date) => moment(date).format('MM/DD/YYYY');
          onInvalidDate(
            `The date "${inputValue}" must be between ${minDate ? bound(minDate) : 'any date'} and ${
              maxDate ? bound(maxDate) : 'any date'
            }`,
            { value: inputValue, reason: 'outOfRange' },
          );
          handleDateChange('');
          onChange({ target: { value: '' } });
        } else {
          onChange(e);
        }
      } else {
        onChange(e);
      }

      if ((!inputValue || inputValue?.length < 10) && hasFutureValidation) {
        onChange(e);
      }
    };

    // Remove the click handler from input - we don't want input clicks to open calendar
    const handleInputClick = (e) => {
      // Just focus the input, don't open calendar
      e.target.focus();
    };

    return (
      <Styled.CustomInputWrapper>
        <MaskedTextInput
          type="text"
          mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
          value={value}
          onChange={handleInputChange}
          onClick={handleInputClick} // Changed: no longer opens calendar
          tabIndex={0}
          onKeyDown={(e) => handleKeyPress(e, onClick)}
          disabled={isActive}
          readOnly={isActive}
          id={idInput}
          autoFocus={isFocus}
          aria-labelledby={inputFor}
          onBlur={() => setIsFocus(false)}
          ref={ref}
        />
        <Styled.CalendarButton
          type="button"
          onClick={handleCalendarButtonClick}
          disabled={isActive}
          aria-label="Open calendar"
        />
      </Styled.CustomInputWrapper>
    );
  });

  return (
    <Styled.InputWrap
      ref={wrapperRef}
      className="custom-react-date-picker"
      isMargin={isMargin}
      isFullWidth={isFullWidth}
      pageColumns={pageColumns}
    >
      <Styled.CalendarLabel htmlFor={idInput} id={inputFor} aria-label={label}>
        {label}
      </Styled.CalendarLabel>
      <Styled.ReactPickerWrapper isError={required && error}>
        <DatePicker
          ref={datePickerRef}
          required={required}
          selected={selected}
          onChange={handleDateChange}
          dateFormatCalendar={dateFormatCalendar}
          maxDate={maxDate}
          minDate={minDate}
          onCalendarClose={handleCalendarClose}
          onCalendarOpen={handleCalendarOpen}
          // showMonthYearDropdown={showMonthYearDropdown}
          renderCustomHeader={showMonthYearDropdown ? renderCustomHeader : undefined}
          customInput={<ExampleCustomInput />}
          popperProps={{ strategy: 'fixed' }}
          {...props}
        />
        {error && <Styled.Error>{error}</Styled.Error>}
      </Styled.ReactPickerWrapper>
    </Styled.InputWrap>
  );
};

Picker.propTypes = {
  idInput: PropTypes.string.isRequired,
  inputFor: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.object,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  label: PropTypes.string,
  required: PropTypes.bool,
  dateFormatCalendar: PropTypes.string,
  showMonthYearDropdown: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isFullWidth: PropTypes.bool,
  isMargin: PropTypes.bool,
  isActive: PropTypes.bool,
  hasFutureValidation: PropTypes.bool,
  onInvalidDate: PropTypes.func,
  pageColumns: PropTypes.string,
};

Picker.defaultProps = {
  minDate: new Date('01-01-1980'.replace(/-/g, '/')),
  maxDate: new Date(),
  label: '',
  required: false,
  dateFormatCalendar: 'MMMM yyyy',
  showMonthYearDropdown: false,
  error: false,
  isFullWidth: false,
  isMargin: false,
  isActive: false,
  hasFutureValidation: PropTypes.false,
  selected: null,
  onInvalidDate: null,
  pageColumns: '',
};

export default memo(Picker);
